// Pricing calculation engine — mirrors backend logic for live preview in funnel
export const DOOR_SERVICES = new Set(["sliding_door_rescreen", "new_sliding_door"]);

export function meshPriceForService(mesh, serviceKey) {
  if (!mesh) return 0;
  if (DOOR_SERVICES.has(serviceKey)) {
    if (mesh.door_price_per_screen != null && mesh.door_price_per_screen !== "") {
      return Number(mesh.door_price_per_screen) || 0;
    }
  }
  return Number(mesh.price_per_screen) || 0;
}

export function calculateEstimate({ services, companySettings }) {
  const s = companySettings || {};
  const serviceCfg = s.services || {};
  const meshUpgrades = s.mesh_upgrades || [];
  const minimum_service_fee = s.minimum_service_fee || 0;
  const trip_fee = s.trip_fee || 0;
  const taxEnabled = !!s.tax_enabled;
  const taxRate = s.tax_rate || 0;
  const tiers = (s.bulk_discount_tiers || []).slice().sort((a, b) => b.min_qty - a.min_qty);

  const lineItems = [];
  let subtotal = 0;
  let taxableBase = 0;       // sum of taxable line amounts BEFORE discount
  let totalQty = 0;

  for (const svc of services) {
    const cfg = serviceCfg[svc.service_key];
    if (!cfg || !cfg.enabled) continue;
    const qty = Number(svc.quantity) || 0;
    if (qty <= 0) continue;
    // Per-service taxable flag; default true so existing configs keep working.
    const taxable = cfg.taxable !== false;
    totalQty += qty;
    const unit = Number(cfg.price_per_unit) || 0;
    const base = unit * qty;
    subtotal += base;
    if (taxable) taxableBase += base;
    lineItems.push({
      label: serviceLabel(svc.service_key),
      qty,
      unit_price: unit,
      amount: base,
      taxable,
    });

    // Mesh upgrade (service-aware pricing for sliding doors)
    if (svc.mesh_id) {
      const mesh = meshUpgrades.find((m) => m.id === svc.mesh_id);
      if (mesh && !mesh.is_default) {
        const pricePerScreen = meshPriceForService(mesh, svc.service_key);
        if (pricePerScreen > 0) {
          const meshAmount = pricePerScreen * qty;
          subtotal += meshAmount;
          // Mesh upgrade inherits the parent service's taxable flag.
          if (taxable) taxableBase += meshAmount;
          lineItems.push({
            label: `${mesh.name} upgrade`,
            qty,
            unit_price: pricePerScreen,
            amount: meshAmount,
            taxable,
          });
        }
      }
    }
  }

  // Trip fee: most states don't tax flat service fees, so default non-taxable.
  if (trip_fee > 0) {
    subtotal += trip_fee;
    lineItems.push({ label: "Trip fee", qty: 1, unit_price: trip_fee, amount: trip_fee, taxable: false });
  }

  // Bulk discount applies proportionally to the whole order. We prorate the
  // share of the discount that lands on taxable items so tax is computed
  // on the correctly-reduced taxable base.
  let discountPercent = 0;
  for (const t of tiers) {
    if (totalQty >= t.min_qty) { discountPercent = t.percent; break; }
  }
  const discount = subtotal * (discountPercent / 100);
  let afterDiscount = subtotal - discount;
  const taxableShare = subtotal > 0 ? taxableBase / subtotal : 0;
  let taxableAfterDiscount = taxableBase - discount * taxableShare;

  // Minimum service fee — top-up amount not part of taxable goods.
  let minApplied = 0;
  if (afterDiscount < minimum_service_fee && totalQty > 0) {
    minApplied = minimum_service_fee - afterDiscount;
    afterDiscount = minimum_service_fee;
  }

  const tax = taxEnabled && taxRate > 0 ? Math.max(0, taxableAfterDiscount) * taxRate : 0;
  const total = afterDiscount + tax;

  return {
    subtotal: round2(subtotal),
    discount: round2(discount),
    discount_percent: discountPercent,
    minimum_applied: round2(minApplied),
    taxable_subtotal: round2(taxableAfterDiscount),
    tax: round2(tax),
    tax_rate: taxRate,
    total: round2(total),
    line_items: lineItems,
    total_quantity: totalQty,
  };
}

function round2(n) { return Math.round(n * 100) / 100; }

export function serviceLabel(key) {
  return {
    window_rescreen: "Window Screen Rescreen",
    new_window_screen: "New Window Screen",
    sliding_door_rescreen: "Sliding Screen Door Rescreen",
    new_sliding_door: "New Sliding Screen Door",
  }[key] || key;
}

export const SERVICE_KEYS = ["window_rescreen", "new_window_screen", "sliding_door_rescreen", "new_sliding_door"];
